import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../contexts/ThemeContext';
import i18n from '../i18n';
import { AddCircleIcon, CloseIcon } from './PrebookingIcons';

/**
 * EmailListManager Component
 * Allows users to add and remove email addresses with validation
 * Displays emails as removable chips/tags
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
  height: 40px;
  background-color: #f6f8fb;
  color: #333333;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(69, 68, 107, 0.6);
  }

  &:focus {
    outline: none;
    border-color: #45446b;
    color: #45446b;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  height: 40px;
  border: 1px solid rgba(69, 68, 107, 0.4);
  background-color: white;
  border-radius: 20px;
  color: #45446b;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: #45446b;
    background-color: rgba(69, 68, 107, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const EmailChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const EmailChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f6f8fb;
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 50px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  color: #45446b;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(234, 126, 143, 0.1);
    border-color: rgba(234, 126, 143, 0.5);
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: rgba(69, 68, 107, 0.6);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #EA7E8F;

    svg path {
      stroke: #EA7E8F;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorText = styled.span`
  color: ${({ colors }) => colors?.coral || '#EA7E8F'};
  font-size: 12px;
  margin-top: 4px;
`;

const EmailListManager = ({ emails, onAdd, onRemove, placeholder, addButtonText, pinnedEmail }) => {
  const { colors } = useContext(ThemeContext);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  // Email validation regex
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAdd = () => {
    // Clear previous errors
    setError('');

    // Trim whitespace
    const email = inputValue.trim();

    // Validate not empty
    if (!email) {
      setError(i18n('email_required') || 'Email is required');
      return;
    }

    // Validate email format
    if (!validateEmail(email)) {
      setError(i18n('invalid_email') || 'Invalid email address');
      return;
    }

    // Check for duplicates (including the pinned email)
    if (emails.includes(email) || (pinnedEmail && email === pinnedEmail)) {
      setError(i18n('email_already_added') || 'Email already added');
      return;
    }

    // Add email
    onAdd(email);

    // Clear input
    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (index) => {
    onRemove(index);
    setError('');
  };

  return (
    <Container>
      <InputRow>
        <EmailInput
          type="email"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError('');
          }}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || i18n('enter_email') || 'Enter email address'}
        />
        <AddButton onClick={handleAdd} disabled={!inputValue.trim()}>
          <AddCircleIcon color={colors.iconAccentColor} size={16} />
          {addButtonText || i18n('add') || 'Add'}
        </AddButton>
      </InputRow>

      {error && <ErrorText colors={colors}>{error}</ErrorText>}

      {(pinnedEmail || (emails && emails.length > 0)) && (
        <EmailChipsContainer>
          {pinnedEmail && (
            <EmailChip>
              <span>{pinnedEmail}</span>
            </EmailChip>
          )}
          {emails && emails.map((email, index) => (
            <EmailChip key={index}>
              <span>{email}</span>
              <RemoveButton
                onClick={() => handleRemove(index)}
                title={i18n('remove') || 'Remove'}
              >
                <CloseIcon color="#45446b" size={16} />
              </RemoveButton>
            </EmailChip>
          ))}
        </EmailChipsContainer>
      )}
    </Container>
  );
};

EmailListManager.propTypes = {
  emails: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  addButtonText: PropTypes.string,
  pinnedEmail: PropTypes.string,
};

export default EmailListManager;
